/**
 * 블럭 카테고리(actor / content / event / product / place / structure / decision) 의 색상 SSOT.
 *
 * 두 가지 표면을 함께 다룬다:
 * - DOM CSS: `cat-${category}` 클래스 (`src/dashboard/src/styles/ontology.css` 정의)
 * - Canvas 2D: `buildCategoryColor()` 가 mount 시점에 `--pk-*` 토큰을 hex 로 resolve
 *
 * 두 표면이 같은 토큰을 가리키도록 단일 매핑 테이블 (`CATEGORY_TOKENS`) 만 유지하고,
 * Canvas 용 resolve 헬퍼는 그 매핑을 그대로 따라간다. CSS 클래스는 ontology.css 에
 * 정적으로 선언되어 있으므로 본 파일에서 직접 다루지 않으나, 매핑이 일치한다는 사실을
 * 주석으로 명시해 토큰을 변경할 때 두 곳을 함께 수정하도록 가이드한다.
 *
 * SSR / private mode 에서 토큰 미정의 시 fallback hex 사용.
 *
 * @see src/dashboard/src/styles/ontology.css `.cat-actor` 외 7종
 */
import type { BlockCategory } from "../api/admin-client.ts"

/**
 * 카테고리 → primary 색 토큰명 매핑.
 * CSS `.cat-${category}` 의 background `color-mix(in srgb, var(--pk-*-500) ...)` 와 일치.
 */
export const CATEGORY_TOKENS: Readonly<Record<BlockCategory, string>> = {
	actor: "--pk-purple-500",
	content: "--pk-teal-500",
	event: "--pk-orange-500",
	product: "--pk-brand-500",
	place: "--pk-red-500",
	structure: "--pk-gray-500",
	decision: "--pk-yellow-500"
}

/**
 * 토큰 미정의 환경 (SSR/private mode) 에서 사용하는 fallback hex.
 * CSS `.cat-${category}` 가 정의된 토큰의 시각적 근사.
 */
export const CATEGORY_FALLBACK_HEX: Readonly<Record<BlockCategory, string>> = {
	actor: "#8b5cf6",
	content: "#06b6d4",
	event: "#f97316",
	product: "#00db63",
	place: "#ef4444",
	structure: "#6b7280",
	decision: "#eab308"
}

/**
 * `--pk-*` 토큰 값을 :root 에서 읽어 hex/rgb 문자열로 반환.
 *
 * Canvas 2D 는 `var(--pk-*)` 함수를 직접 받지 못하므로 mount 시 한 번 resolve 한다.
 * SSR 또는 토큰 미정의 환경에서는 빈 문자열을 반환 — 호출처는 fallback hex 와 `||` 로 OR.
 *
 * @param name CSS 토큰 이름 (예: "--pk-purple-500")
 * @returns 해석된 색 문자열 (예: "#a234c7") 또는 빈 문자열
 */
export function cssVar(name: string): string {
	if (typeof window === "undefined") return ""
	return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

/**
 * Master Graph 카테고리 고정 색상 — Canvas 2D 용 hex/rgb 맵 빌드.
 *
 * Canvas 2D 가 `var(--pk-*)` 를 직접 못 받기 때문에 컴포넌트 mount 시점에 토큰을
 * resolve 한다. 토큰 미정의 (SSR/private mode) 시 fallback hex 사용.
 *
 * @returns `Record<BlockCategory, string>` — 모든 카테고리에 대해 hex/rgb 색 보장
 */
export function buildCategoryColor(): Record<BlockCategory, string> {
	const out = {} as Record<BlockCategory, string>
	for (const cat of Object.keys(CATEGORY_TOKENS) as BlockCategory[]) {
		out[cat] = cssVar(CATEGORY_TOKENS[cat]) || CATEGORY_FALLBACK_HEX[cat]
	}
	return out
}
